import {
  formattedFamilySearchData,
  RawFamilySearchPersonResults,
  PostIndi,
  useFamilyTreeAddReport,
} from '@ltvco/refresh-lib/fmt';
import { Stack, Text, Grid, Box } from '@ltvco/refresh-lib/theme';
import { titleize, useScreenSize } from '@ltvco/refresh-lib/utils';
import { Record } from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  ConsolidatedSearchResultCard,
} from '@ltvco/refresh-lib/v2';
import React, { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { constants } from 'appConstants';

import { MasonryCardsContainer } from './Cards/MasonryCardsContainer';
import { MobileGenealogyCard as MobileGenealogyCardVariation1 } from './Cards/NewCardsDesignVariation1/GenealogyCards/MobileGenealogyCard';
import { useSearchPersonResultsContext } from './context';

interface FamilySearchResultsProps {
  familySearchInfo: AdvancedSearchURLParams;
  familySearchData: RawFamilySearchPersonResults;
}

export const FamilySearchResults = ({
  familySearchInfo,
  familySearchData,
}: FamilySearchResultsProps) => {
  const [ip, setIp] = useState<string>('');
  const [searchParams] = useSearchParams();
  const { isMobile } = useScreenSize();
  const { meetMobileCardInDesktopCriteria } = useSearchPersonResultsContext();
  const { handleAddToFamilyTree, isReportInTree, isCreatingFamilyTree } =
    useFamilyTreeAddReport();

  useEffect(() => {
    document.title = 'Search - BeenVerified';
  }, []);

  useEffect(() => {
    if (!constants.featureFlags.familySearchEnabled) {
      return;
    }
    (async () => {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();

      setIp(data.ip);
    })();
  }, []);

  let personResults: Record[] = [];

  let formattedFamily: any = [];

  if (familySearchData) {
    formattedFamily = formattedFamilySearchData(
      familySearchData,
      familySearchInfo.fName,
      familySearchInfo.lName
    );
  }
  personResults = formattedFamily;

  // Utils
  const getFullName = () => {
    const { fName, mName, lName } = familySearchInfo;

    const nameData = [fName, mName, lName].filter(Boolean);
    const fullName = nameData.join(' ');

    return titleize(fullName.toLowerCase());
  };

  let obitRedirectMessage: string | null = null;

  if (searchParams.get('redirected_from_obit')) {
    obitRedirectMessage = `Looks like we don't have obituary search data for ${getFullName()}, but we have person search results for similar names`;
  }

  function title() {
    if (obitRedirectMessage) {
      return (
        <Text variant="h3" display={'inline'}>
          {obitRedirectMessage}
        </Text>
      );
    }
  }

  const Container = meetMobileCardInDesktopCriteria
    ? MasonryCardsContainer
    : Fragment;

  if (isMobile || meetMobileCardInDesktopCriteria) {
    return (
      <Container>
        {personResults.map((person: Record, index: number) => {
          const fsId = person.familySearchId ?? '';
          const reportInFamilyTree = fsId
            ? isReportInTree(fsId, 'family_search_id')
            : false;

          return (
            <React.Fragment key={index}>
              <MobileGenealogyCardVariation1
                {...person}
                personData={person}
                reportInTree={reportInFamilyTree}
                isCreatingFamilyTree={isCreatingFamilyTree}
                handleAddToFamilyTree={(postIndi: PostIndi) =>
                  handleAddToFamilyTree(
                    postIndi,
                    fsId ?? '',
                    'family_search_id'
                  )
                }
              />
            </React.Fragment>
          );
        })}
      </Container>
    );
  }

  return (
    <Stack sx={{ minHeight: 800, position: 'relative' }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12 }} sx={{ mt: { xs: 8.75, md: 0 } }}>
          <Grid size={{ xs: 12 }}>
            <Box
              mb={{ xs: 2, md: 2 }}
              ml={{ xs: 2, md: 0 }}
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
            >
              {title()}
            </Box>
          </Grid>
          {personResults.map((person: Record, index: number) => {
            const fsId = person.familySearchId ?? '';
            const reportInFamilyTree = fsId
              ? isReportInTree(fsId, 'family_search_id')
              : false;

            // Limit the number of relatives to 5
            const limitedRelatives = person.relatives?.slice(0, 5);

            // Limit the number of fsIds to 5
            const allFsIds = Array.isArray(person.relativesNotSerialized)
              ? person.relativesNotSerialized
                  .map((relative) => relative.fsId)
                  .slice(0, 5)
                  .join(', ')
              : '';

            return (
              <React.Fragment key={index}>
                <ConsolidatedSearchResultCard
                  {...person}
                  personData={person}
                  ip={ip}
                  relatives={limitedRelatives}
                  isConsolidatedSearch={true}
                  ancestorButton={person.familySearchId ? true : false}
                  reportInTree={reportInFamilyTree}
                  textButtonProp={person.familySearchId ? 'View' : ''}
                  relativeFsId={allFsIds}
                  tabName="Family Search"
                  handleAddToFamilyTree={(postIndi: PostIndi) =>
                    handleAddToFamilyTree(
                      postIndi,
                      fsId ?? '',
                      'family_search_id'
                    )
                  }
                />
              </React.Fragment>
            );
          })}
        </Grid>
      </Grid>
    </Stack>
  );
};
